/* eslint-disable import/no-duplicates */
//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { BehaviorSubject } from 'rxjs';

import { IInstagramUserStatsActions } from '@instagram-account/instagram-account-actions/instagram-user-stats-actions';
import { UniversalImageActionsRightsRequestOAuthService } from '@legacy-components/common/universal-image-actions/universal-image-actions-rights-request-oauth-service';
import { SearchFilterService } from '@legacy-components/filters/search-filter-service';
import PartialCuid from '@models/PartialCuid';
import { UniversalImageActionsView } from '@models/UniversalImageActionsView';
import getAngularInjector from '@react-bridge/getAngularInjector';
import { IUniversalImageActionsService } from '@react-bridge/wrappers/ng-service-provider-types';
import { CollectionsSettingsService } from '@services/collections-settings-service';
import { DateTimeService } from '@services/date-time-service';
import GoogleAnalyticsConnectionService from '@services/google-analytics-connection-service';
import { InstagramSettingsService } from '@services/instagram-settings-service';
import MetricsGatewayApiService from '@services/metrics-gateway-api-service';
import { TStreamsDrawerActions } from '@streams-drawer/actions/streams-drawer-actions';

import { IBulkActionsActions } from 'app/bulk-actions/bulk-actions-actions/bulk-actions-actions.js';
import { IAllItemDetails } from 'app/bulk-actions/model/AppState';
import { IPromotedPostsMetricsActions } from 'app/promoted-posts-metrics/promoted-posts-metrics-actions/promoted-posts-metrics-actions';

// Importing an Angular class as a type doesn't ensure that service is properly compiled,
// so we also import every full file to make sure it exists when injected.
import '@instagram-account/instagram-account-actions/instagram-user-stats-actions';
import '@legacy-components/common/universal-image-actions/universal-image-actions-rights-request-oauth-service';
import '@services/experiences-integrations-service';
import '@services/google-analytics-connection-service';
import '@services/instagram-settings-service';
import '@services/metrics-gateway-api-service';
import 'app/promoted-posts-metrics/promoted-posts-metrics-actions/promoted-posts-metrics-actions';
import '@services/collections-settings-service';

export function getGoogleAnalyticsConnectionService(): GoogleAnalyticsConnectionService {
  return getAngularInjector().get('GoogleAnalyticsConnectionService');
}

export function getInstagramSettingsService(): InstagramSettingsService {
  return getAngularInjector().get('InstagramSettingsService');
}

export function getSearchFilterService(): SearchFilterService {
  return getAngularInjector().get('SearchFilterService');
}

export function getUniversalImageActionsService(): IUniversalImageActionsService {
  return getAngularInjector().get('UniversalImageActionsService');
}

export function getUniversalImageActionsView(): typeof UniversalImageActionsView {
  return getAngularInjector().get('UniversalImageActionsView');
}

export function getBulkActionsActions(): IBulkActionsActions {
  return getAngularInjector().get('BulkActionsActions');
}

export function getMetricsGatewayApiService(): MetricsGatewayApiService {
  return getAngularInjector().get('MetricsGatewayApiService');
}

export function getCollectionsSettingsService(): CollectionsSettingsService {
  return getAngularInjector().get('CollectionsSettingsService');
}

// TODO: Convert RightsManagementService to ts and export its requests separately
export function getRightsManagementService(): any {
  return getAngularInjector().get('RightsManagementService');
}

export function getUniversalImageActionsOauthService(): UniversalImageActionsRightsRequestOAuthService {
  return getAngularInjector().get(
    'UniversalImageActionsRightsRequestOAuthService',
  );
}

export function getDateTimeService(): DateTimeService {
  return getAngularInjector().get('DateTimeService');
}

export function getInstagramUserStatsActions(): IInstagramUserStatsActions {
  return getAngularInjector().get('InstagramUserStatsActions');
}

export function getPromotedPostsMetricsActions(): IPromotedPostsMetricsActions {
  return getAngularInjector().get('PromotedPostsMetricsActions');
}

export function getCollectionFiltersConverter(): any {
  return getAngularInjector().get('CollectionFiltersConverter');
}

export function getContentLibraryApiService(): any {
  return getAngularInjector().get('ContentLibraryApiService');
}

export function getStreamsDrawerActions(): TStreamsDrawerActions {
  return getAngularInjector().get('StreamsDrawerActions');
}

export function getContentItemDetailsService(): {
  requestItemDetails: (
    pcuid: PartialCuid,
    forceRefresh: boolean,
  ) => BehaviorSubject<IAllItemDetails | undefined>;
} {
  return getAngularInjector().get('ContentLibraryItemDetailsService');
}
